import React from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { isCommunitySponsor } from '../../config/r2rCommercialSeller';
import { IconDate, IconReviewStar, IconShipping } from '../../components';

import css from './ProfileContactSection.module.css';

const MessageIcon = () => (
  <svg
    className={css.messageIconSvg}
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden
    focusable="false"
  >
    <path
      d="M21 12a8.5 8.5 0 0 1-8.5 8.5H5l-3 3V12A8.5 8.5 0 1 1 21 12Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
    />
  </svg>
);

const MailIcon = () => (
  <svg
    className={css.mailIconSvg}
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden
    focusable="false"
  >
    <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="2" />
    <path d="m4 7 8 6 8-6" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
  </svg>
);

const ListingsIcon = () => (
  <svg
    className={css.statIconSvg}
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden
    focusable="false"
  >
    <path d="M4 7h16M4 12h16M4 17h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const SalesIcon = () => (
  <svg
    className={css.statIconSvg}
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden
    focusable="false"
  >
    <path
      d="M4 19V5M4 19h16M8 15V9m4 6V7m4 8v-4"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Profile-tab contact block.
 * - banner: compact horizontal row when About is empty
 * - aside: sidebar card with stats when About has content
 *
 * @component
 */
const ProfileContactSection = props => {
  const {
    user,
    displayName,
    contactHref,
    variant = 'banner',
    activeListingsCount = 0,
    showItemsSold = false,
    itemsSoldCount = 0,
    className,
  } = props;
  const intl = useIntl();

  if (!contactHref || !displayName) {
    return null;
  }

  const publicData = user?.attributes?.profile?.publicData || {};
  const metadata = user?.attributes?.profile?.metadata || {};
  const createdAt = user?.attributes?.createdAt;
  const location =
    typeof publicData.profileLocation === 'string' ? publicData.profileLocation.trim() : '';
  const reviewAverage = metadata.reviewAverage;
  const reviewCount = metadata.reviewCount;
  const hasRating =
    typeof reviewAverage === 'number' && typeof reviewCount === 'number' && reviewCount > 0;
  const showSponsor = isCommunitySponsor(user);
  const memberYear =
    createdAt instanceof Date || (createdAt && !Number.isNaN(Date.parse(createdAt)))
      ? intl.formatDate(createdAt, { year: 'numeric' })
      : null;

  const messageLabel = (
    <FormattedMessage id="ProfilePage.messageSeller" values={{ name: displayName }} />
  );

  if (variant === 'aside') {
    return (
      <aside className={classNames(css.aside, className)}>
        <div className={css.asideHeader}>
          <div className={css.asideHeaderTop}>
            <div className={css.asideHeaderText}>
              <h3 className={css.asideTitle}>
                <FormattedMessage id="ProfilePage.getInTouchTitle" />
              </h3>
              <p className={css.asideBlurb}>
                <FormattedMessage id="ProfilePage.getInTouchBlurb" values={{ name: displayName }} />
              </p>
            </div>
            <span className={css.asideMailIcon} aria-hidden>
              <MailIcon />
            </span>
          </div>
          <a
            className={css.messageButton}
            href={contactHref}
            target="_blank"
            rel="noopener noreferrer"
          >
            {messageLabel}
          </a>
        </div>

        <dl className={css.statList}>
          <div className={css.statRow}>
            <dt>
              <ListingsIcon />
              <FormattedMessage id="ProfilePage.contactStatActiveListings" />
            </dt>
            <dd>{activeListingsCount}</dd>
          </div>
          {showItemsSold ? (
            <div className={css.statRow}>
              <dt>
                <SalesIcon />
                <FormattedMessage id="ProfilePage.contactStatTotalSales" />
              </dt>
              <dd>{itemsSoldCount}</dd>
            </div>
          ) : null}
          {hasRating ? (
            <div className={css.statRow}>
              <dt>
                <IconReviewStar className={css.statIconStar} isFilled isAriaHidden />
                <FormattedMessage id="ProfilePage.contactStatAvgRating" />
              </dt>
              <dd>
                <FormattedMessage
                  id="ProfilePage.contactStatAvgRatingValue"
                  values={{ average: reviewAverage }}
                />
              </dd>
            </div>
          ) : null}
          {location ? (
            <div className={css.statRow}>
              <dt>
                <IconShipping className={css.statIcon} />
                <FormattedMessage id="ProfilePage.contactStatShipsFrom" />
              </dt>
              <dd>{location}</dd>
            </div>
          ) : null}
          {memberYear ? (
            <div className={css.statRow}>
              <dt>
                <IconDate className={css.statIcon} />
                <FormattedMessage id="ProfilePage.contactStatMemberSince" />
              </dt>
              <dd>{memberYear}</dd>
            </div>
          ) : null}
        </dl>
      </aside>
    );
  }

  // Banner variant — empty About
  const subtitleParts = [];
  if (showSponsor) {
    subtitleParts.push(<FormattedMessage key="sponsor" id="ProfilePage.communitySponsorBadge" />);
  }
  if (showItemsSold) {
    subtitleParts.push(
      <FormattedMessage
        key="sales"
        id="ProfilePage.statSalesCount"
        values={{ count: itemsSoldCount }}
      />
    );
  }

  return (
    <section className={classNames(css.banner, className)}>
      <div className={css.bannerIcon} aria-hidden>
        <MessageIcon />
      </div>
      <div className={css.bannerText}>
        <h3 className={css.bannerTitle}>
          <FormattedMessage id="ProfilePage.reachOutTitle" values={{ name: displayName }} />
        </h3>
        {subtitleParts.length > 0 ? (
          <p className={css.bannerSubtitle}>
            {subtitleParts.map((part, index) => (
              <React.Fragment key={index}>
                {index > 0 ? <span className={css.subtitleSep}> · </span> : null}
                {part}
              </React.Fragment>
            ))}
          </p>
        ) : null}
      </div>
      <a
        className={classNames(css.messageButton, css.bannerMessageButton)}
        href={contactHref}
        target="_blank"
        rel="noopener noreferrer"
      >
        {messageLabel}
      </a>
    </section>
  );
};

export default ProfileContactSection;
